import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, CornerDownLeft, Loader2, X, FileText, UserPlus, Check } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { formatMoney0 } from "../../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v, cur = "AUD") => formatMoney0(v, cur);
const BILLING_SUFFIX = { one_off: "", monthly: "/mo", yearly: "/yr" };

export default function QuoteAssistant({ onCreated }) {
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [proposal, setProposal] = useState(null);   // editable draft proposal
  const [creating, setCreating] = useState(false);

  const buildProposalState = (d) => {
    const custOpts = [
      ...d.customer.matches.map((m) => ({ value: `${m.type}:${m.id}`, label: `${m.name} · ${m.type}`, name: m.name, type: m.type, id: m.id })),
      ...(d.customer.suggest_new_prospect ? [{ value: "__new__", label: `Create new prospect "${d.customer.suggest_new_prospect}"`, name: d.customer.suggest_new_prospect, type: "new" }] : []),
    ];
    return {
      customerQuery: d.customer.query,
      custOpts,
      customerChoice: custOpts[0]?.value || "",
      catalogue: d.catalogue || [],
      items: (d.line_items || []).map((li) => {
        const m = li.matches?.[0];
        return { code: m?.code || "", qty: li.qty || 1, unit_price: m?.unit_price ?? 0, billing: m?.billing || "one_off", description: m?.name || li.query, query: li.query };
      }),
    };
  };

  const ask = async () => {
    const question = q.trim();
    if (!question || asking) return;
    setAsking(true); setProposal(null);
    setThread((t) => [...t, { role: "user", content: question }]);
    setQ("");
    try {
      const r = await axios.post(`${API}/quotes/assistant`, { question, session_id: sessionId });
      setSessionId(r.data.session_id);
      if (r.data.mode === "proposal") {
        setThread((t) => [...t, { role: "assistant", content: `Here's a draft for “${r.data.customer.query}” — review and create it below.` }]);
        setProposal(buildProposalState(r.data));
      } else {
        setThread((t) => [...t, { role: "assistant", content: r.data.answer, citations: r.data.citations || [] }]);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Assistant unavailable";
      toast.error(msg);
      setThread((t) => [...t, { role: "assistant", content: msg, error: true }]);
    } finally { setAsking(false); }
  };
  const newChat = () => { setThread([]); setSessionId(null); setQ(""); setProposal(null); };

  const setItem = (i, patch) => setProposal((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const pickProduct = (i, code) => {
    const c = proposal.catalogue.find((x) => x.code === code);
    if (c) setItem(i, { code: c.code, unit_price: c.unit_price, billing: c.billing, description: c.name });
  };
  const addItem = () => setProposal((p) => ({ ...p, items: [...p.items, { code: "", qty: 1, unit_price: 0, billing: "one_off", description: "", query: "" }] }));
  const removeItem = (i) => setProposal((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const totals = () => {
    const g = { one_off: 0, monthly: 0, yearly: 0 };
    (proposal?.items || []).forEach((it) => { g[it.billing] = (g[it.billing] || 0) + (Number(it.qty) || 0) * (Number(it.unit_price) || 0); });
    return g;
  };

  const createDraft = async () => {
    const chosen = proposal.custOpts.find((o) => o.value === proposal.customerChoice);
    if (!chosen) { toast.error("Pick a customer"); return; }
    const valid = proposal.items.filter((it) => it.code && Number(it.qty) > 0);
    if (!valid.length) { toast.error("Add at least one product"); return; }
    setCreating(true);
    try {
      let link_type = chosen.type, link_id = chosen.id, link_name = chosen.name;
      if (chosen.type === "new") {
        const pr = await axios.post(`${API}/sales/prospects`, { company_name: chosen.name });
        const p = pr.data.prospect || pr.data;
        link_type = "prospect"; link_id = p.id; link_name = p.company_name || chosen.name;
      }
      const body = {
        link_type, link_id, link_name,
        line_items: valid.map((it) => ({ code: it.code, description: it.description, qty: Number(it.qty), unit_price: Number(it.unit_price), billing: it.billing })),
        valid_until: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      };
      const res = await axios.post(`${API}/quotes`, body);
      toast.success(`Draft created · ${res.data.quote.quote_number}`);
      setThread((t) => [...t, { role: "assistant", content: `✓ Created draft ${res.data.quote.quote_number} for ${link_name}.` }]);
      setProposal(null);
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create draft"); }
    finally { setCreating(false); }
  };

  const g = totals();

  return (
    <Card className="bg-[#FFFCF2] border-[#ECE1C2] shadow-card rounded-xl overflow-hidden" data-testid="quote-assistant">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles className="w-4 h-4 text-[#B8860B]" />
          <span className="text-[13px] font-semibold text-[#1A1A1A]">Quote assistant</span>
          {thread.length === 0 && <span className="text-[11px] text-[#8A857A]">query quotes or say "create a quote for Whyte Cargo for 2x EQUIPR Core"</span>}
          {thread.length > 0 && <button onClick={newChat} className="ml-auto text-[11px] text-[#8A857A] hover:text-[#1A1A1A] flex items-center gap-1" data-testid="quote-ask-newchat"><X className="w-3 h-3" /> New chat</button>}
        </div>

        {thread.length > 0 && (
          <div className="mb-3 space-y-2.5 max-h-[320px] overflow-y-auto pr-1" data-testid="quote-ask-thread">
            {thread.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end"><div className="bg-[#FFD108] text-[#1A1A1A] rounded-2xl rounded-br-sm px-3.5 py-2 text-[13px] font-medium max-w-[80%]" data-testid={`quote-ask-user-${i}`}>{m.content}</div></div>
              ) : (
                <div key={i} className="flex justify-start"><div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[90%] shadow-sm" data-testid={`quote-ask-assistant-${i}`}>
                  <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.error ? "text-[#C23A00]" : "text-[#2A2A2A]"}`}>{m.content}</p>
                  {m.citations?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.citations.map((c, ci) => (
                        <span key={ci} className="inline-flex items-center gap-1.5 rounded-md bg-[#FBFAF7] border border-[#E6E2D9] px-2 py-1 text-[11px]" data-testid={`quote-cite-${ci}`}>
                          <FileText className="w-3 h-3 text-[#B8860B]" /><span className="mono font-semibold text-[#1A1A1A]">{c.quote_number}</span>
                          <span className="text-[#8A857A]">{c.name} · {c.status} · {money(c.amount, c.currency)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div></div>
              )
            ))}
            {asking && <div className="flex justify-start"><div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2 text-[#8A857A] text-[13px]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div></div>}
          </div>
        )}

        {/* Draft proposal */}
        {proposal && (
          <div className="mb-3 rounded-lg border border-[#E2DCCF] bg-white p-3.5" data-testid="quote-proposal">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A6D00] mb-2">Draft proposal</div>
            <label className="text-[11px] uppercase tracking-wide text-[#8A857A] font-semibold block mb-1">Customer</label>
            {proposal.custOpts.length === 0 ? (
              <p className="text-[12px] text-[#C23A00]">No customer match for "{proposal.customerQuery}".</p>
            ) : (
              <Select value={proposal.customerChoice} onValueChange={(v) => setProposal((p) => ({ ...p, customerChoice: v }))}>
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] h-9 text-sm" data-testid="quote-proposal-customer"><SelectValue /></SelectTrigger>
                <SelectContent>{proposal.custOpts.map((o) => <SelectItem key={o.value} value={o.value}>{o.type === "new" ? <span className="flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> {o.label}</span> : o.label}</SelectItem>)}</SelectContent>
              </Select>
            )}

            <label className="text-[11px] uppercase tracking-wide text-[#8A857A] font-semibold block mt-3 mb-1">Line items</label>
            <div className="space-y-2">
              {proposal.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`quote-proposal-item-${i}`}>
                  <Select value={it.code || undefined} onValueChange={(v) => pickProduct(i, v)}>
                    <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] h-9 text-sm flex-1" data-testid={`quote-proposal-product-${i}`}><SelectValue placeholder={it.query || "Select product"} /></SelectTrigger>
                    <SelectContent className="max-h-[280px]">{proposal.catalogue.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} · {money(c.unit_price)}{BILLING_SUFFIX[c.billing]}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="w-16 bg-[#F4F2ED] border-[#DAD5CA] h-9 text-sm" title="Qty" data-testid={`quote-proposal-qty-${i}`} />
                  <Input type="number" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} className="w-24 bg-[#F4F2ED] border-[#DAD5CA] h-9 text-sm" title="Unit price" data-testid={`quote-proposal-price-${i}`} />
                  <button onClick={() => removeItem(i)} className="w-8 h-8 shrink-0 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid={`quote-proposal-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={addItem} className="text-[11px] text-[#8A6D00] font-semibold hover:underline" data-testid="quote-proposal-additem">+ Add line</button>
            </div>

            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-[#F0EDE6]">
              <span className="mono text-[12px] text-[#1A1A1A]">
                {[g.one_off && `${money(g.one_off)} one-off`, g.monthly && `${money(g.monthly)}/mo`, g.yearly && `${money(g.yearly)}/yr`].filter(Boolean).join("  ·  ") || "—"}
              </span>
              <Button onClick={createDraft} disabled={creating} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid="quote-proposal-create">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />} Create draft</Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !asking) ask(); }} placeholder={thread.length ? "Ask a follow-up or create another…" : "Query quotes or create one…"} className="bg-white border-[#DAD5CA] text-[#1A1A1A] placeholder:text-[#8A857A] pr-9" data-testid="quote-ask-input" />
            <CornerDownLeft className="w-3.5 h-3.5 text-[#B8B2A5] absolute right-3 top-1/2 -translate-y-1/2" />
          </div>
          <Button onClick={ask} disabled={asking || !q.trim()} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid="quote-ask-btn">{asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}<span className="ml-1.5">Ask</span></Button>
        </div>
      </CardContent>
    </Card>
  );
}
